import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import { refreshFilesFromApi } from "@/store/thunks/sixDetail/refreshFilesThunk";
import { setUploadProgress } from "@/store/slices/poc/heliaSlice";

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 15000;
const MAX_POLL_DURATION_MS = 60000;
const JITTER_RANGE = 0.4;
const EXPONENTIAL_BASE = 2;
const JITTER_DIVISOR = 2;

const pendingCids = new Map<string, Set<string>>();
const activePolls = new Map<string, Promise<boolean>>();

async function delay(ms: number): Promise<void> {
  // eslint-disable-next-line promise/avoid-new -- setTimeout requires Promise wrapper
  await new Promise(resolve => { setTimeout(resolve, ms); });
}

function calculateBackoffDelayMs(retryAttemptCount: number): number {
  const exponentialDelayMs = BASE_DELAY_MS * Math.pow(EXPONENTIAL_BASE, retryAttemptCount);
  const cappedDelayMs = Math.min(exponentialDelayMs, MAX_DELAY_MS);
  const jitterOffsetMs = cappedDelayMs * (Math.random() * JITTER_RANGE - JITTER_RANGE / JITTER_DIVISOR);
  return Math.max(0, Math.floor(cappedDelayMs + jitterOffsetMs));
}

function addPendingCid(sixId: string, cid: string): void {
  let cidSet = pendingCids.get(sixId);
  if (cidSet === undefined) {
    cidSet = new Set();
    pendingCids.set(sixId, cidSet);
  }
  cidSet.add(cid);
}

function removePendingCid(sixId: string, cid: string): void {
  const cidSet = pendingCids.get(sixId);
  if (cidSet !== undefined) {
    cidSet.delete(cid);
    if (cidSet.size === 0) {
      pendingCids.delete(sixId);
    }
  }
}

function getPendingCids(sixId: string): Set<string> {
  return pendingCids.get(sixId) ?? new Set();
}

function cleanupPoll(sixId: string): void {
  activePolls.delete(sixId);
  pendingCids.delete(sixId);
}

export const pollForClonedFiles = createAppAsyncThunk<
  boolean,
  { sixId: string; cid: string }
>(
  "sixDetail/pollForClonedFiles",
  async({ sixId, cid }, { dispatch, getState, extra }) => {
    const log = extra.loggerService.createLogger({ namespace: 'six3ro:six:poll-cloned' });

    // Check for existing poll FIRST to avoid race condition
    const existingPoll = activePolls.get(sixId);
    if (existingPoll !== undefined) {
      addPendingCid(sixId, cid);
      log.info(`[${sixId}] Added CID ${cid} to existing poll`);
      return await existingPoll;
    }

    // No existing poll - create one
    addPendingCid(sixId, cid);
    log.info(`[${sixId}] Starting poll for cloned file CID: ${cid}`);

    const pollPromise = (async(): Promise<boolean> => {
      const startTime = Date.now();
      let retryAttempt = 0;

      while (Date.now() - startTime < MAX_POLL_DURATION_MS) {
        const pending = getPendingCids(sixId);
        if (pending.size === 0) {
          log.info(`[${sixId}] All pending CIDs found`);
          cleanupPoll(sixId);
          return true;
        }

        const result = await dispatch(refreshFilesFromApi(sixId)).unwrap();

        if (!result.skipped) {
          const fileCids = new Set(result.files.map(f => f.cid));

          for (const pendingCid of pending) {
            if (fileCids.has(pendingCid)) {
              log.info(`[${sixId}] Found cloned file with CID: ${pendingCid}`);

              // Update upload progress to complete (100%)
              const currentUploadProgress = getState().helia.uploadProgress;
              const uploadEntry = Object.values(currentUploadProgress).find(
                (progress) => progress.cid === pendingCid
              );
              if (uploadEntry !== undefined) {
                dispatch(setUploadProgress({
                  fileName: uploadEntry.fileName,
                  cid: pendingCid,
                  stage: 'complete',
                  progress: 100
                }));
              }

              removePendingCid(sixId, pendingCid);
            }
          }

          if (getPendingCids(sixId).size === 0) {
            log.info(`[${sixId}] All pending CIDs found`);
            cleanupPoll(sixId);
            return true;
          }
        }

        const delayMs = calculateBackoffDelayMs(retryAttempt);
        log.debug(`[${sixId}] Waiting ${delayMs}ms before next poll (attempt ${retryAttempt + 1})`);
        await delay(delayMs);
        retryAttempt++;
      }

      log.warn(`[${sixId}] Poll timeout - CIDs not found: ${[...getPendingCids(sixId)].join(', ')}`);
      cleanupPoll(sixId);
      return false;
    })();

    activePolls.set(sixId, pollPromise);
    return await pollPromise;
  }
);
